'use client';

import { useEffect, useRef } from 'react';
import { useClerk, useSignIn, useSignUp } from '@clerk/nextjs';
import { LoadingMorph } from '@extern/ui/loading-morph';
import { useSessionStorage } from '@uidotdev/usehooks';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { usePasswordlessSignUpVariant } from '@/app/(auth)/_hooks/use-passwordless-sign-up-variant';
import { persistLastAuthenticationStrategy } from '@/app/(auth)/_utils/auth-last-authentication-strategy';
import { describeIncompleteSsoSignUp, describeSsoAttempt, resolveSsoSignUpRecovery, type SsoTransferError, ssoSignUpFallbackPath, ssoSignUpVerifyEmailPath } from '@/app/(auth)/_utils/incomplete-sso-sign-up';
import { resetStaleOAuthState } from '@/app/(auth)/_utils/reset-stale-oauth-state';
import { useSignInFlowStore } from '@/app/(auth)/sign-in/_hooks/use-sign-in-flow-store';
import { resolveSecondFactorStrategy } from '@/app/(auth)/sign-in/_utils/resolve-second-factor-strategy';
import { useSignUpFlowStore } from '@/app/(auth)/sign-up/_hooks/use-sign-up-flow-store';
import { completeSignUp } from '@/app/(auth)/sign-up/_utils/complete-sign-up';
import { portfolioClaimSignUpMetadata } from '@/features/portfolio/_utils/portfolio-claim-origin';
import { resolvePortfolioAttribution } from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import { useUtmParams } from '@/hooks/use-utm-params';
import { AUTH_ERROR_QUERY_KEY, AUTH_ERROR_QUERY_VALUE_SSO_FAILED, DEFAULT_LOGGED_PAGE, SIGNUP_ACCOUNT_CREATION_METHOD_KEY, SIGNUP_NEWSLETTER_KEY } from '@/infra/constants/routes';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { ErrorTracking } from '@/lib/error-tracking';
import { useGetFeatureFlag } from '@/lib/feature-flags/hooks/use-get-feature-flag';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { resolvePostAuthRedirectUrl, resolvePostSignUpRedirectUrl } from '@/utils/resolve-post-sign-up-redirect-url';

/** How recently a session must have been active to be the one the Google step just created. */
const RECENT_SESSION_MS = 2 * 60 * 1000;
function buildSsoFailedUrl(path: string): string {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(AUTH_ERROR_QUERY_KEY, AUTH_ERROR_QUERY_VALUE_SSO_FAILED);
  return `${url.pathname}${url.search}`;
}
async function redirectSsoFailed(router: ReturnType<typeof useRouter>, path: string, signIn: ReturnType<typeof useSignIn>['signIn'], signUp: ReturnType<typeof useSignUp>['signUp'], clerkClient: ReturnType<typeof useClerk>['client']): Promise<void> {
  await resetStaleOAuthState({
    signIn,
    signUp,
    clerkClient
  });
  router.push(buildSsoFailedUrl(path));
}
export function SSOCallback() {
  const router = useRouter();
  const [newsletter, setNewsletter] = useSessionStorage<boolean | null>(SIGNUP_NEWSLETTER_KEY, null);
  const [, setAccountCreationMethod] = useSessionStorage<'email' | 'google' | null>(SIGNUP_ACCOUNT_CREATION_METHOD_KEY, null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSignUpCallback = pathname?.includes('/sign-up/sso-callback');
  const {
    campaign,
    content,
    medium,
    source,
    referrer,
    partner,
    portfolioCode,
    portfolioUrl,
    searchParamFallbacks
  } = useUtmParams();
  const allowlistedParams = getAllowlistedSearchParams(searchParams, searchParamFallbacks);
  const portfolioAttribution = resolvePortfolioAttribution({
    portfolioCode,
    portfolioUrl,
    utmContent: content,
    referrer: null
  });
  const {
    trackSsoSignInCompleted,
    trackSsoSignUpCompleted,
    trackSsoTransferUsed,
    trackSsoExistingSessionReused,
    trackSsoFallbackToSignIn,
    trackSsoFallbackToSignUp,
    trackSsoSignUpRecoveryStarted,
    trackAuthError,
    identifyAuthUser
  } = useAuthEvents();
  const clerk = useClerk();
  const {
    signIn
  } = useSignIn();
  const {
    signUp
  } = useSignUp();
  const hasRun = useRef(false);
  const hasActivatedSignUpSession = useRef(false);
  const {
    track
  } = useAnalytics();
  const freemiumVariant = useGetFeatureFlag('ab_test_freemium');
  // Fails closed: an arm not yet known gets control's behaviour.
  const {
    isPasswordless
  } = usePasswordlessSignUpVariant();
  const handleSignInComplete = (session: {
    currentTask?: unknown;
    user?: {
      id?: string;
      emailAddresses?: {
        emailAddress?: string;
      }[];
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  }) => {
    useSignInFlowStore.getState().onChange({
      step: 'start'
    });
    if (session?.currentTask) {
      return;
    }
    identifyAuthUser({
      userId: session?.user?.id,
      email: session?.user?.emailAddresses?.[0]?.emailAddress,
      firstName: session?.user?.firstName,
      lastName: session?.user?.lastName,
      accountCreationMethod: 'google'
    });
    trackSsoSignInCompleted();
    persistLastAuthenticationStrategy('oauth_google');
    router.push(resolvePostAuthRedirectUrl(searchParams, DEFAULT_LOGGED_PAGE));
  };
  const mirrorSignUpMetadataToClerk = async (): Promise<void> => {
    const mirrored: Record<string, unknown> = {
      ...portfolioClaimSignUpMetadata(content)
    };
    if (partner) {
      mirrored.partner = partner;
    }
    if (portfolioAttribution) {
      mirrored.portfolio_attribution = portfolioAttribution;
    }
    if (freemiumVariant !== undefined) {
      mirrored.ab_test_freemium = freemiumVariant;
    }
    if (Object.keys(mirrored).length === 0 || !clerk.user) {
      return;
    }
    try {
      await clerk.user.update({
        unsafeMetadata: {
          ...clerk.user.unsafeMetadata,
          ...mirrored
        }
      });
    } catch (error) {
      trackAuthError('sign_up', 'sso_callback', 'sso_mirror_unsafe_metadata_failed', {
        error_message: error instanceof Error ? error.message : String(error)
      });
      ErrorTracking.captureMessage('sso_mirror_unsafe_metadata_failed', {
        tags: {
          auth_flow: 'sign_up',
          auth_step: 'sso_callback',
          error_code: 'sso_mirror_unsafe_metadata_failed'
        },
        extra: {
          error_message: error instanceof Error ? error.message : String(error)
        }
      });
    }
  };
  const handleSignUpComplete = async (session_0: {
    currentTask?: unknown;
    getToken: () => Promise<string | null>;
    reload: () => Promise<unknown>;
    user?: {
      id?: string;
      emailAddresses?: {
        emailAddress?: string;
      }[];
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  }) => {
    useSignUpFlowStore.getState().onChange({
      step: 'start'
    });
    if (session_0?.currentTask) {
      return;
    }
    await mirrorSignUpMetadataToClerk();
    await completeSignUp({
      userId: session_0?.user?.id ?? '',
      email: session_0?.user?.emailAddresses?.[0]?.emailAddress ?? '',
      firstName: session_0?.user?.firstName ?? '',
      lastName: session_0?.user?.lastName ?? '',
      newsletter: newsletter ?? false,
      accountCreationMethod: 'google',
      allowlistedParams,
      campaign,
      medium,
      source,
      referrer,
      partner,
      portfolioAttribution,
      utmContent: content,
      freemiumVariant,
      tracking: {
        identifyAuthUser,
        track,
        onCompleted: () => {
          trackSsoSignUpCompleted();
          persistLastAuthenticationStrategy('oauth_google');
        }
      },
      clearStoredNewsletter: () => setNewsletter(null),
      clearStoredAccountCreationMethod: () => setAccountCreationMethod(null),
      redirect: () => {
        router.push(resolvePostSignUpRedirectUrl(searchParams));
      }
    });
  };
  const finalizeSignIn = async (): Promise<void> => {
    await signIn.finalize({
      navigate: ({
        session: session_1
      }) => handleSignInComplete(session_1)
    });
  };
  const finalizeAndCompleteSignUp = async (): Promise<void> => {
    hasActivatedSignUpSession.current = true;
    let signUpFinalizeSession: Parameters<typeof handleSignUpComplete>[0] | null = null;
    await signUp.finalize({
      navigate: ({
        session: session_2
      }) => {
        signUpFinalizeSession = session_2;
      }
    });
    if (signUpFinalizeSession) {
      await handleSignUpComplete(signUpFinalizeSession);
    }
  };
  const failToSignIn = async (errorCode: string, diagnostics?: Record<string, string>): Promise<void> => {
    trackSsoFallbackToSignIn();
    trackAuthError('sign_in', 'sso_callback', errorCode, diagnostics);
    await redirectSsoFailed(router, '/sign-in', signIn, signUp, clerk.client);
  };
  const failToSignUp = async (errorCode_0: string, diagnostics_0?: Record<string, string>): Promise<void> => {
    trackSsoFallbackToSignUp();
    trackAuthError('sign_up', 'sso_callback', errorCode_0, diagnostics_0);
    await redirectSsoFailed(router, '/sign-up', signIn, signUp, clerk.client);
  };
  const isUnsupportedFirstFactor = (): boolean => signIn.status === 'needs_first_factor' && !signIn.supportedFirstFactors?.every(f => f.strategy === 'enterprise_sso');
  const handleUnsupportedFirstFactor = async (): Promise<void> => {
    if (isSignUpCallback) {
      await failToSignUp('sso_first_factor_not_supported');
      return;
    }
    await failToSignIn('sso_first_factor_not_supported');
  };
  const transferToSignIn = async (): Promise<void> => {
    trackSsoTransferUsed('sign_up_to_sign_in');
    await signIn.create({
      transfer: true
    });
    if (String(signIn.status) === 'complete') {
      await finalizeSignIn();
      return;
    }
    await failToSignIn('sso_fallback_sign_in');
  };
  const transferToSignUp = async (): Promise<void> => {
    trackSsoTransferUsed('sign_in_to_sign_up');
    // Clerk returns this error rather than throwing it.
    const {
      error: transferError
    } = await signUp.create({
      transfer: true
    });
    if (signUp.status === 'complete') {
      await finalizeAndCompleteSignUp();
      return;
    }
    await recoverIncompleteSignUp({
      errorCode: 'sso_fallback_sign_up',
      failInControl: failToSignUp,
      transferError
    });
  };
  const sendToSignUpWithEmail = async (errorCode_1: string, diagnostics_1: Record<string, string>): Promise<void> => {
    const emailAddress = signUp.emailAddress;
    trackSsoFallbackToSignUp();
    trackAuthError('sign_up', 'sso_callback', errorCode_1, {
      ...diagnostics_1,
      sso_email_prefilled: Boolean(emailAddress),
      sso_recovery: 'email_fallback'
    });
    if (emailAddress) {
      trackSsoSignUpRecoveryStarted('email_fallback', diagnostics_1);
    }
    await redirectSsoFailed(router, ssoSignUpFallbackPath({
      allowlistedParams,
      emailAddress,
      newsletter
    }), signIn, signUp, clerk.client);
  };

  // Records why Clerk left a Google sign-up unfinished. The passwordless treatment then carries the person on
  // (code screen, or sign-up with the Google address); control keeps its bounce, so the arms stay comparable.
  /** A session the transfer had already produced when our (stale) sign-up object read as empty. */
  type SessionClerkAlreadyCreated = {
    kind: 'completed_sign_up';
    sessionId: string;
  } | {
    kind: 'active_session';
    sessionId: string;
  };

  /**
   * 🔴 THE "EMPTY" GOOGLE FAILURES WERE MOSTLY SUCCESSES. #3065's logging (2026-09-25/26): after a transfer
   * that returned no error, `signUp` read as no sign-up at all, and 5 of the first 7 people had an account
   * within the hour, ~17 seconds later on average. Clerk had finished the sign-up; the object this
   * component holds was stale. `clerk.client` is the fresh state, so ask it before calling anything a failure.
   */
  const findSessionClerkAlreadyCreated = (): SessionClerkAlreadyCreated | null => {
    const client = clerk.client;
    if (!client) {
      return null;
    }
    if (client.signUp?.status === 'complete' && client.signUp.createdSessionId) {
      return {
        kind: 'completed_sign_up',
        sessionId: client.signUp.createdSessionId
      };
    }
    // A session active in the last two minutes can only have come from the Google step we just ran. Anything
    // older belongs to someone who was already signed in, and that is not ours to decide here.
    const recent = client.signedInSessions?.find(session_3 => session_3.status === 'active' && Date.now() - session_3.lastActiveAt.getTime() < RECENT_SESSION_MS);
    return recent ? {
      kind: 'active_session',
      sessionId: recent.id
    } : null;
  };

  /**
   * 🔴 THE TRANSFER'S ANSWER CAN GO MISSING ON THE WAY BACK. clerk-js 6.34 `_baseFetch` returns null, with no
   * error, when the browser reports itself offline mid-request, and `fromJSON(null)` then changes nothing: the
   * sign-up reads empty although FAPI created the session. Every treatment transfer "failure" with diagnostics
   * since #3076 (4 of 4, Sep 28-29) carried a session under two minutes old. Fetching the client again is how
   * this page gets to see it; `clerk.client` updates in place.
   */
  const reloadClerkClient = async (): Promise<'yes' | 'failed'> => {
    try {
      await clerk.client?.reload();
      return 'yes';
    } catch {
      return 'failed';
    }
  };

  /**
   * Same finish as a Google sign-up that read `complete` first time: provisioning, the sign-up events, and the
   * post-sign-up URL. The earlier `active_session` finish only set the session and pushed the URL; the app then
   * had no provisioned user behind that session and sent the person to /sign-in (seen 2026-09-28: a 48-second
   * detour before they signed in with Google a second time).
   */
  const finishSessionClerkAlreadyCreated = async (found: SessionClerkAlreadyCreated): Promise<void> => {
    hasActivatedSignUpSession.current = true;
    let createdSession: Parameters<typeof handleSignUpComplete>[0] | null = null;
    await clerk.setActive({
      session: found.sessionId,
      navigate: ({
        session: session_4
      }) => {
        createdSession = session_4;
      }
    });
    if (createdSession) {
      await handleSignUpComplete(createdSession);
    }
  };
  const recoverIncompleteSignUp = async ({
    errorCode: errorCode_2,
    failInControl,
    transferError: transferError_0
  }: {
    errorCode: string;
    failInControl: (errorCode_2: string, diagnostics: Record<string, string>) => Promise<void>;
    transferError?: SsoTransferError | null;
  }): Promise<void> => {
    const diagnostics_2: Record<string, string> = {
      ...describeIncompleteSsoSignUp(signUp, transferError_0),
      // Treatment only (Jay, 2026-09-25), like the recovery itself: control logs exactly what #3036 logged.
      ...(isPasswordless ? describeSsoAttempt({
        signIn,
        signUp
      }) : {}),
      ...(isPasswordless ? {
        client_sign_up_status: clerk.client?.signUp?.status ?? 'none',
        client_signed_in_sessions: String(clerk.client?.signedInSessions?.length ?? 0)
      } : {})
    };
    ErrorTracking.captureMessage(errorCode_2, {
      level: 'warning',
      tags: {
        auth_flow: 'sign_up',
        auth_step: 'sso_callback',
        error_code: errorCode_2
      },
      extra: diagnostics_2
    });
    if (!isPasswordless) {
      await failInControl(errorCode_2, diagnostics_2);
      return;
    }
    let alreadyCreated = findSessionClerkAlreadyCreated();
    if (!alreadyCreated) {
      diagnostics_2.sso_client_reloaded = await reloadClerkClient();
      diagnostics_2.client_signed_in_sessions_after_reload = String(clerk.client?.signedInSessions?.length ?? 0);
      alreadyCreated = findSessionClerkAlreadyCreated();
    }
    if (alreadyCreated) {
      // Logged on the same event as every other outcome, so the arms still compare on one count.
      trackAuthError('sign_up', 'sso_callback', errorCode_2, {
        ...diagnostics_2,
        sso_recovery: alreadyCreated.kind
      });
      trackSsoSignUpRecoveryStarted(alreadyCreated.kind, diagnostics_2);
      await finishSessionClerkAlreadyCreated(alreadyCreated);
      return;
    }
    if (resolveSsoSignUpRecovery(signUp) === 'verify_email') {
      const {
        error: sendError
      } = await signUp.verifications.sendEmailCode();
      if (!sendError) {
        trackAuthError('sign_up', 'sso_callback', errorCode_2, {
          ...diagnostics_2,
          sso_recovery: 'verify_email'
        });
        trackSsoSignUpRecoveryStarted('verify_email', diagnostics_2);
        // The code screen finishes the account and reads its method from session storage.
        setAccountCreationMethod('google');
        // Tells the sign-up page to keep this code screen through its mount reset.
        useSignUpFlowStore.getState().onChange({
          step: 'email-verification',
          resumeAtEmailVerification: true
        });
        router.push(ssoSignUpVerifyEmailPath(allowlistedParams));
        return;
      }
      diagnostics_2.sso_send_code_error_code = sendError.code ?? 'unknown';
    }
    await sendToSignUpWithEmail(errorCode_2, diagnostics_2);
  };

  // Only reached with a sign-up in progress, so in the treatment both callbacks recover to sign-up.
  const handleMissingRequirements = async (): Promise<void> => {
    await recoverIncompleteSignUp({
      errorCode: 'sso_missing_requirements_fallback',
      failInControl: isSignUpCallback ? failToSignUp : failToSignIn
    });
  };
  const reuseExistingSession = async (): Promise<void> => {
    const sessionId = signIn.existingSession?.sessionId || signUp.existingSession?.sessionId;
    if (!sessionId) {
      return;
    }
    if (isSignUpCallback) {
      useSignUpFlowStore.getState().onChange({
        step: 'choose'
      });
      const chooseUrl = allowlistedParams ? `/sign-up?step=choose&${allowlistedParams}` : '/sign-up?step=choose';
      router.push(chooseUrl);
      return;
    }
    trackSsoExistingSessionReused();
    await clerk.setActive({
      session: sessionId,
      navigate: ({
        session: session_5
      }) => {
        useSignInFlowStore.getState().onChange({
          step: 'start'
        });
        if (session_5?.currentTask) {
          return;
        }
        persistLastAuthenticationStrategy('oauth_google');
        router.push(resolvePostAuthRedirectUrl(searchParams, '/'));
      }
    });
  };
  const hasMissingRequirements = (): boolean => signIn.status === 'needs_identifier' && signUp.status === 'missing_requirements';
  const needsSecondFactorVerification = (): boolean => signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust';
  const needsSignInFallbackFactor = (): boolean => signIn.status === 'needs_new_password';
  const buildSignInVerificationsUrl = (): string => {
    const url = new URL('/sign-in', window.location.origin);
    url.searchParams.set('step', 'verifications');
    if (allowlistedParams) {
      for (const [key, value] of new URLSearchParams(allowlistedParams)) {
        url.searchParams.set(key, value);
      }
    }
    return `${url.pathname}${url.search}`;
  };
  const redirectToSignInSecondFactor = async (): Promise<void> => {
    const secondFactorStrategy = await resolveSecondFactorStrategy(signIn);
    if (!secondFactorStrategy) {
      await failToSignIn('sso_second_factor_not_available');
      return;
    }
    useSignInFlowStore.getState().onChange({
      step: 'verifications',
      secondFactorStrategy,
      firstFactorMethod: 'google',
      identifier: signIn.identifier ?? ''
    });
    router.push(buildSignInVerificationsUrl());
  };
  const hasReusableExistingSession = (): boolean => Boolean(signIn.existingSession || signUp.existingSession);
  const handleUnhandledSsoState = async (): Promise<void> => {
    const flow = isSignUpCallback ? 'sign_up' : 'sign_in';
    const fallbackPath = isSignUpCallback ? '/sign-up' : '/sign-in';
    trackAuthError(flow, 'sso_callback', 'sso_unhandled_state', isPasswordless ? {
      ...describeSsoAttempt({
        signIn,
        signUp
      })
    } : undefined);
    await redirectSsoFailed(router, fallbackPath, signIn, signUp, clerk.client);
  };
  const reportSsoError = async (err: unknown): Promise<void> => {
    const flow_0 = isSignUpCallback ? 'sign_up' : 'sign_in';
    trackAuthError(flow_0, 'sso_callback', 'sso_unhandled_error', {
      error_message: err instanceof Error ? err.message : String(err)
    });
    ErrorTracking.captureMessage('sso_unhandled_error', {
      tags: {
        auth_flow: flow_0,
        auth_step: 'sso_callback',
        error_code: 'sso_unhandled_error'
      },
      extra: {
        error_message: err instanceof Error ? err.message : String(err)
      }
    });
    const fallbackUrl = isSignUpCallback || hasActivatedSignUpSession.current ? '/sign-up' : '/sign-in';
    const redirectUrl = allowlistedParams ? `${fallbackUrl}?${allowlistedParams}` : fallbackUrl;
    await redirectSsoFailed(router, redirectUrl, signIn, signUp, clerk.client);
  };
  const resolveSsoCallback = async (): Promise<void> => {
    hasActivatedSignUpSession.current = false;
    try {
      if (signIn.status === 'complete') {
        await finalizeSignIn();
        return;
      }
      if (signUp.isTransferable) {
        await transferToSignIn();
        return;
      }
      if (isUnsupportedFirstFactor()) {
        await handleUnsupportedFirstFactor();
        return;
      }
      if (signIn.isTransferable) {
        await transferToSignUp();
        return;
      }
      if (hasMissingRequirements()) {
        await handleMissingRequirements();
        return;
      }
      if (signUp.status === 'complete') {
        await finalizeAndCompleteSignUp();
        return;
      }
      if (needsSecondFactorVerification()) {
        await redirectToSignInSecondFactor();
        return;
      }
      if (needsSignInFallbackFactor()) {
        await failToSignIn('sso_needs_new_password');
        return;
      }
      if (hasReusableExistingSession()) {
        await reuseExistingSession();
        return;
      }
      await handleUnhandledSsoState();
    } catch (err_0) {
      await reportSsoError(err_0);
    }
  };
  const resolveSsoCallbackRef = useRef(resolveSsoCallback);
  resolveSsoCallbackRef.current = resolveSsoCallback;
  useEffect(() => {
    if (!clerk.loaded || hasRun.current) {
      return;
    }
    hasRun.current = true;
    resolveSsoCallbackRef.current();
  }, [clerk.loaded]);
  return <div className="flex min-h-screen items-center justify-center">
      <LoadingMorph />
      <div id="clerk-captcha" />
    </div>;
}